/**
 * `pattern.progressRing` (tokens.json / today-view.md SS1.2) — Screen 1's
 * percentage-complete ring, borrowed from "Surface". Track = border.strong,
 * fill/stroke = hue.primary.vivid (graphical tier, 3:1 WCAG floor). The
 * numeric value is exposed via aria-label even though the visual treatment
 * is ambient (today-view.md SS5: "accessibility doesn't inherit 'ambient' as
 * an excuse to hide the fact from assistive tech").
 *
 * Center label (today-view.md SS8, v2.7 — owner hands-on finding): a raw
 * percentage forces the reader to do division in their head to know "how
 * many are actually left" ("58%" of what?). Passing `completed`/`total`
 * renders the real count as a fraction (`9/10`) instead — the arc fill
 * still tracks `percent` (still a meaningful ambient "how full is this"
 * read), only the printed label changes. Falls back to `${percent}%` when
 * `completed`/`total` aren't supplied, so this stays a general-purpose ring.
 */
export function ProgressRing({
  percent,
  completed,
  total,
  size = 72,
}: {
  readonly percent: number;
  readonly completed?: number;
  readonly total?: number;
  readonly size?: number;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const strokeWidth = 5;
  const radius = size / 2 - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (circumference * clamped) / 100;
  const showsFraction = completed !== undefined && total !== undefined;
  const label = showsFraction ? `${completed}/${total}` : `${clamped}%`;

  return (
    <div
      role="img"
      aria-label={
        showsFraction
          ? `${completed} of ${total} of today's actions complete`
          : `${clamped} percent of today's actions complete`
      }
      style={{ position: 'relative', width: size, height: size, flex: 'none' }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border-strong)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--hue-primary-vivid)"
          strokeWidth={strokeWidth}
          // Bug fix (owner hands-on finding, "blown out at 100%"): a
          // rounded linecap on a FULL circle (offset 0, start and end caps
          // at the same point) doubles up into a visible raised bump right
          // at the 12-o'clock seam — an artifact that only exists at
          // exactly 100%. Every partial value keeps the round cap (it's
          // the correct, softer look for an in-progress arc); only the
          // closed-loop case switches to a flush butt cap, which has no
          // seam to bump.
          strokeLinecap={clamped === 100 ? 'butt' : 'round'}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset var(--dur-counter) var(--ease-standard)',
          }}
        />
      </svg>
      <div
        aria-hidden="true"
        className="num"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Sized against the ring's default 72px footprint (up from 48px
          // specifically to fit a fraction label — see the width-safety
          // note below) at a fixed type.caption (13px) size: tokens.md's
          // reading-text floor is 13px, and this ring must hold up to 7
          // characters ("100/100") legibly, not just "100%"'s 4. Conservative
          // width check (today-view.md SS8, same 0.6em-per-character
          // methodology already used for the phone mood tile's width math,
          // Inter's own tabular-figure advance width): 7 x 13 x 0.6 =
          // 54.6px against this ring's (size - 2*strokeWidth) = 62px content
          // budget -- 7.4px / ~12% headroom, positive at every value up to
          // and including "100/100". whiteSpace/lineHeight are
          // belt-and-suspenders so the string can never wrap inside the
          // flex-centered box.
          fontSize: 'var(--type-caption-size)',
          fontWeight: 'var(--type-body-weight-emphasis)',
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}
      >
        {label}
      </div>
    </div>
  );
}
